// <div data-controller="category-videos">
//   <div class="relative">
//     <video muted playsinline data-category-videos-target="video" data-action="click->category-videos#togglePlay"></video>
//     <div class="hidden" data-category-videos-target="playButton" data-action="click->category-videos#togglePlay">...</div>
//     <div class="hidden pointer-events-none opacity-0 group-hover:opacity-100" data-category-videos-target="pauseButton">...</div>
//     <button data-category-videos-target="muteButton" data-action="click->category-videos#toggleMute">
//       <svg data-icon="muted">...</svg>
//       <svg data-icon="unmuted" class="hidden">...</svg>
//     </button>
//   </div>
//   ...one wrapper per entry video
// </div>
//
// Instagram-style autoplay: when nothing is playing, the most visible video
// (at least half on screen) starts; a playing video keeps going until it
// scrolls mostly out of view, and only one plays at a time. Hidden tabs pause.
// One mute state for the whole page, starting muted. A video the visitor
// paused, or that played to the end, stays stopped until they tap it again
// (no loop, so muted autoplay can't rack up watch time). Nothing autoplays
// until the visitor's first wheel/touch/pointer/key input — crawlers scroll by
// script, which fires none of these, so they never rack up video views.
// Each play stamps data-autoplayed so the analytics
// listeners in application.js can flag autoplay vs. tapped plays.
import { Controller } from "@hotwired/stimulus"

const PLAY_RATIO = 0.5
const PAUSE_RATIO = 0.25
const INTERACTIONS = ["wheel", "touchstart", "pointerdown", "keydown"]

export default class extends Controller {
  static targets = ["video", "playButton", "pauseButton", "muteButton"]

  // Not connect(): Stimulus fires videoTargetConnected before connect().
  initialize() {
    this.muted = true
    this.interacted = false
    this.ratios = new Map()
    this.userPaused = new WeakSet()
    this.observer = new IntersectionObserver((entries) => this.observe(entries), {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    })
    this.onVisibilityChange = () => this.playMostVisible()
    this.onInteraction = (event) => this.interact(event)
  }

  connect() {
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    if (!this.interacted) this.listenForInteraction(true)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    this.listenForInteraction(false)
  }

  videoTargetConnected(video) {
    video.muted = this.muted
    video.addEventListener("play", () => this.played(video))
    video.addEventListener("pause", () => this.showPlayButton(video, true))
    this.observer.observe(video)
  }

  videoTargetDisconnected(video) {
    this.observer.unobserve(video)
    this.ratios.delete(video)
  }

  togglePlay(event) {
    const video = event.currentTarget.parentElement.querySelector("video")

    if (video.paused) {
      this.userPaused.delete(video)
      this.play(video, false)
    } else {
      this.userPaused.add(video)
      video.pause()
    }
  }

  toggleMute() {
    this.muted = !this.muted
    this.videoTargets.forEach((video) => (video.muted = this.muted))
    this.muteButtonTargets.forEach((button) => {
      button.setAttribute("aria-label", this.muted ? "Unmute videos" : "Mute videos")
      button.querySelector('[data-icon="muted"]').classList.toggle("hidden", !this.muted)
      button.querySelector('[data-icon="unmuted"]').classList.toggle("hidden", this.muted)
    })
  }

  // private

  listenForInteraction(on) {
    INTERACTIONS.forEach((type) => {
      if (on) window.addEventListener(type, this.onInteraction, { capture: true, passive: true })
      else window.removeEventListener(type, this.onInteraction, { capture: true })
    })
  }

  interact(event) {
    if (!event.isTrusted) return

    this.interacted = true
    this.listenForInteraction(false)
    this.playMostVisible()
  }

  observe(entries) {
    entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio))
    this.playMostVisible()
  }

  playMostVisible() {
    this.videoTargets.forEach((video) => {
      if (!video.paused && (document.hidden || this.ratio(video) < PAUSE_RATIO)) video.pause()
    })
    if (!this.interacted || document.hidden || this.videoTargets.some((video) => !video.paused)) return

    const video = this.mostVisible()
    if (video) this.play(video, true)
  }

  mostVisible() {
    let best = null
    let bestRatio = PLAY_RATIO

    this.ratios.forEach((ratio, video) => {
      if (ratio < bestRatio || this.userPaused.has(video) || video.ended) return
      best = video
      bestRatio = ratio
    })
    return best
  }

  ratio(video) {
    return this.ratios.get(video) || 0
  }

  play(video, autoplayed) {
    video.dataset.autoplayed = autoplayed
    // Autoplay can be refused (e.g. iOS Low Power Mode); the play button stays up.
    video.play().catch(() => this.showPlayButton(video, true))
  }

  // One video at a time, however it started.
  played(video) {
    this.showPlayButton(video, false)
    this.videoTargets.forEach((other) => {
      if (other !== video && !other.paused) other.pause()
    })
  }

  // Paused: play button. Playing: the pause icon, which fades in on hover.
  showPlayButton(video, paused) {
    const button = (target) => video.parentElement.querySelector(`[data-category-videos-target="${target}"]`)

    button("playButton").classList.toggle("hidden", !paused)
    button("pauseButton").classList.toggle("hidden", paused)
  }
}
